import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getContractApply, getContractOperationInfo } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { changeReservationStatusAsync, getReservationListAsync } from "src/api/reservation/reservation-api";
import { ReservationListModel, ReservationListParams } from "src/api/reservation/reservation-types";
import { BaseModal, ContentsTitle } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { globalPartnerState } from "src/recoil/partners/atom";
import { useToast } from "src/recoil/toast/hook";
import { Modal, ReservationState, getStatusTextOption, getStatusValue } from "../reservation-types";
import BasicInfoDetail from "./basicInfoDetail/BasicInfoDetail";
import History from "./history/History";

const tabs = [
  { value: "basicInfo", label: "기본정보" },
  { value: "history", label: "변경내역" },
];
/* 
  공용공간 예약 > 상세
 */

export type ReservationType = ReservationListModel & { contractName?: string };

const ReservationDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const partner = useRecoilValue(globalPartnerState);
  const { setBaseModal } = useModal();

  const { openToast } = useToast();
  // location search (url query parameter) 를 읽어서 object 로 변환
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
        decoder: (c) => c,
      }),
    [location],
  );

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return tabs.find((tab) => tab.value === queryParams?.tab);
    }
    return tabs[0];
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = useCallback(
    (tab: { value: string; label: string }) => {
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      newQueryParams.tab = tab.value;

      // query parameter 에 tab 추가
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location, queryParams],
  );

  // 공용공간 예약 상태변경 api
  const { executeAsync: changeReservationState } = useApiOperation(changeReservationStatusAsync);
  const { executeAsync: getContractList } = useApiOperation(getContractApply);

  const { executeAsync: getContractOperation } = useApiOperation(getContractOperationInfo);

  // 공용공간 예약 목록 조회 api
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync);
  const [reservation, setReservation] = useState<ReservationType>();

  const [confirmModal, setConfirmModal] = useState<Modal>({
    isOpen: false,
  });

  const fetchReservation = useCallback(
    async (reservationId: string) => {
      // 공용공간 예약 목록 api 로 상세 조회 대신함
      const isDeleted: any = queryParams.isDeleted;
      const reservationListParams: ReservationListParams = {
        page: 0,
        size: 1,
        id: reservationId,
      };
      const { data } = await getReservationList(reservationListParams);
      if (data.data) {
        const _reservation: ReservationListModel = data?.data?.content[0];
        const { data: contract } = await getContractList({ contractIds: String(_reservation.contract.id) });

        if (contract.data) {
          const contractName = contract.data.content[0].contractName;
          const productName = contract.data.content[0].spaceProductName;

          setReservation({ ..._reservation, contractName: contractName ? contractName : productName });
        }
      }
    },
    [getReservationList, location.search],
  );

  useEffect(() => {
    if (params.id) {
      fetchReservation(params.id);
    }
  }, [params.id, fetchReservation]);

  useEffect(() => {
    const { isCreatedOrUpdated } = location.state ?? {};
    if (!isCreatedOrUpdated) return;
    if (!reservation) return;
    const facilityType = reservation.facility?.type;
    if (!facilityType) return;

    getContractOperation({ id: reservation.contract.id }).then(({ data }) => {
      const isUseVisitorInvite = data.data.content.productManage.isUseVisitorInvite;
      const needShowVisitorInvite = (facilityType === "MEETING_ROOM" || facilityType === "REFRESH_ROOM") && isCreatedOrUpdated && isUseVisitorInvite;
      if (!needShowVisitorInvite) return;
      setBaseModal({
        isOpen: true,
        title: "예약이 완료되었습니다.\n 방문자 초대가 필요하신가요?",
        btnLeftTitle: "초대하기",
        btnRightTitle: "완료",
        onClose: () => {
          let path =
            PagePath.visitor.add
              .replace(":partnerId", String(partner?.id))
              .replace(":contractApplyNumber", reservation.contract.applyNumber)
              .replace(":contractId", String(reservation.contract.id)) + `?reservationId=${reservation.id}`;
          window.open(window.location.origin + path, "_blank", "noreferrer");
          setBaseModal({ isOpen: false });
          delete location.state.isCreatedOrUpdated;
        },
        onClick: () => {
          setBaseModal({ isOpen: false });
          delete location.state.isCreatedOrUpdated;
        },
      });
    });
  }, [location.state, reservation, getContractOperation, setBaseModal, partner?.id]);

  const findStatus = async (value: string) => {
    switch (value) {
      case "USE_CANCELLED":
        setConfirmModal({
          isOpen: true,
          message: "취소로 변경 하시겠습니까?",
          payload: { status: ReservationState.CANCELLED },
        });
    }
  };

  const clickModalConfirm = useCallback(async () => {
    setConfirmModal({ isOpen: false });

    if (!params.id) {
      throw Error("reservationId is not found");
    }
    const { data } = await changeReservationState({
      id: String(params.id),
      statusCode: ReservationState.CANCELLED, // 취소
    });
    if (data?.data?.reservation) {
      openToast({ content: "정상적으로 변경되었습니다." });
      if (params.id) {
        fetchReservation(params.id);
      }
    }
  }, [changeReservationState, confirmModal.payload?.status, fetchReservation, params.id]);

  return (
    <div className="page-product-access">
      <MetaTag subTitle={params.id ? params.id : ""} />
      {confirmModal.isOpen && (
        <BaseModal
          isOpen={confirmModal.isOpen}
          btnLeftTitle="취소"
          btnRightTitle="확인"
          onClose={() => setConfirmModal({ isOpen: false })}
          onClick={() => clickModalConfirm()}
          title={confirmModal.message}
        ></BaseModal>
      )}
      <ContentsTitle
        title="공용공간 예약"
        tabs={tabs}
        activeTab={activeTab}
        clickTab={clickTab}
        detailInfo={[
          { title: "예약 ID", value: params.id ? params.id : "" },
          { title: "예약 그룹 ID", value: queryParams.groupID ? String(queryParams.groupID) : "" },
          // 신청번호는 비노출 (계약 ID는 기본정보 탭의 신청/계약 정보에서 확인한다)
        ]}
        selectValue={getStatusValue(reservation?.status?.code, reservation?.start, reservation?.end) || "-"}
        selectOptions={reservation?.status ? getStatusTextOption(reservation.status.code, reservation.start, reservation.end) : []}
        selectDisabled={reservation?.status?.code === ReservationState.CANCELLED ? true : false}
        setSelectStateValue={(value: string) => findStatus(value)}
        colorType="reservation"
      />
      {/* 탭 */}
      {/* 기본정보 */}
      {activeTab?.value === "basicInfo" && <BasicInfoDetail reservation={reservation} />}
      {activeTab?.value === "history" && <History />}
    </div>
  );
};
export default ReservationDetail;
